import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import config from '../config';

const STATUS_BADGE = {
  success: 'bg-success',
  partial: 'bg-warning text-dark',
  failed: 'bg-danger',
  processing: 'bg-info text-dark',
  pending: 'bg-secondary',
  skipped: 'bg-light text-dark border',
};

const AI_LABELS = {
  openai: { label: 'OpenAI GPT-4o', badge: 'bg-success', icon: '✦' },
  gemini: { label: 'Gemini 1.5', badge: 'bg-info text-dark', icon: '◈' },
  deepseek: { label: 'DeepSeek', badge: 'bg-warning text-dark', icon: '◇' },
  fallback: { label: 'Local Fallback', badge: 'bg-secondary', icon: '○' },
};

function StatusBadge({ status }) {
  const cls = STATUS_BADGE[status] || 'bg-secondary';
  return <span className={`badge ${cls}`}>{status || '—'}</span>;
}

function buildHighlightsFromProduct(p) {
  if (!p || !p.specs) return [];
  const s = p.specs;
  const rows = [];
  const disp = [s.screenCondition, s.scratchesLevel].filter(Boolean).join(' · ');
  if (disp) rows.push({ label: 'Display', value: disp });
  if (typeof s.batteryHealth === 'number') rows.push({ label: 'Battery', value: `${s.batteryHealth}%` });
  if (s.ramGB) rows.push({ label: 'RAM', value: `${s.ramGB} GB` });
  if (s.storageGB) rows.push({ label: 'Storage', value: `${s.storageGB} GB` });
  if (s.processor) rows.push({ label: 'Processor', value: String(s.processor) });
  const cam = [s.rearCameraMP ? `${s.rearCameraMP}MP rear` : '', s.frontCameraMP ? `${s.frontCameraMP}MP front` : '']
    .filter(Boolean).join(' · ');
  if (cam) rows.push({ label: 'Camera', value: cam });
  if (s.batteryMah) rows.push({ label: 'Battery', value: `${s.batteryMah} mAh` });
  if (s.network) rows.push({ label: 'Network', value: s.network });
  if (s.conditionGrade) rows.push({ label: 'Grade', value: s.conditionGrade });
  return rows.slice(0, 6);
}

const EMPTY_SLIDE_TITLES = ['', '', '', '', '', ''];

function SocialMediaDashboard() {
  const API_URL = config.API_URL;

  const [step, setStep] = useState(1);
  const maxStep = 6;
  const [cfg, setCfg] = useState({ autoPostEnabled: false, dailyPostTime: '10:00' });
  const [history, setHistory] = useState([]);
  const [loadingGenerate, setLoadingGenerate] = useState(false);
  const [loadingPost, setLoadingPost] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const [loadingUpload, setLoadingUpload] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [histLoading, setHistLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, msg: '', type: 'success' });
  const [lastResult, setLastResult] = useState(null);
  const [lastAction, setLastAction] = useState(null);

  // Product vs shop promo reel
  const [reelWorkflowMode, setReelWorkflowMode] = useState('product'); // 'product' | 'promo'
  const [promoScriptBrief, setPromoScriptBrief] = useState('');
  const [promoTone, setPromoTone] = useState('professional');

  const clearPreviewState = useCallback(() => {
    setPreviewData(null);
    setEditedVoiceover('');
    setEditedCaption('');
    setEditedSlideTitles([...EMPTY_SLIDE_TITLES]);
  }, []);

  // Product
  const [selectedProductId, setSelectedProductId] = useState('');
  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');

  // Step 2 media
  const [photoFiles, setPhotoFiles] = useState([]);
  const [uploadResponse, setUploadResponse] = useState(null);
  const [outroFile, setOutroFile] = useState(null);
  const [musicFile, setMusicFile] = useState(null);

  // Step 3 highlights
  const [highlights, setHighlights] = useState([]);

  // Step 4 voice/music/theme
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [voiceGender, setVoiceGender] = useState('female');
  const [useDefaultMusic, setUseDefaultMusic] = useState(true);
  const [reelTheme, setReelTheme] = useState('classic');

  // Step 5 – script preview / edit
  const [previewData, setPreviewData] = useState(null);
  const [editedVoiceover, setEditedVoiceover] = useState('');
  const [editedCaption, setEditedCaption] = useState('');
  const [editedSlideTitles, setEditedSlideTitles] = useState([...EMPTY_SLIDE_TITLES]);

  // AI status
  const [aiStatus, setAiStatus] = useState({ primary: 'fallback', openai: false, gemini: false, deepseek: false });

  const showToast = (msg, type = 'success') => {
    setToast({ show: true, msg, type });
    setTimeout(() => setToast(t => ({ ...t, show: false })), 4500);
  };

  const socialApiErr = (e, fallback) => {
    const status = e?.response?.status;
    const msg = e?.response?.data?.message || e.message || fallback;
    if (status === 404 && /not found/i.test(msg)) {
      return 'Social API connect nahi ho rahi — backend reload karo ya admin deploy chalao';
    }
    return msg;
  };

  const copyManualPost = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast(`${label} text copy ho gaya — ab paste kar lo`, 'success');
    } catch (e) {
      showToast('Clipboard block ho sakta hai — niche se text select karke copy karo', 'warning');
    }
  };

  const fetchConfig = useCallback(async () => {
    try {
      const resp = await axios.get(`${API_URL}/social/config`);
      setCfg({ autoPostEnabled: Boolean(resp.data?.autoPostEnabled), dailyPostTime: resp.data?.dailyPostTime || '10:00' });
    } catch (e) {}
  }, [API_URL]);

  const fetchHistory = useCallback(async () => {
    setHistLoading(true);
    try {
      const resp = await axios.get(`${API_URL}/social/history?limit=20`);
      setHistory(Array.isArray(resp.data) ? resp.data : []);
    } finally { setHistLoading(false); }
  }, [API_URL]);

  const fetchProducts = useCallback(async () => {
    try {
      const resp = await axios.get(`${API_URL}/products?limit=2000&sortBy=createdAt&sortOrder=desc`);
      const list = Array.isArray(resp.data) ? resp.data : [];
      // API already newest-first; secondary: higher stock first among same time
      list.sort((a, b) => {
        const ta = new Date(a?.createdAt || 0).getTime();
        const tb = new Date(b?.createdAt || 0).getTime();
        if (tb !== ta) return tb - ta;
        return (Number(b?.stock) || 0) - (Number(a?.stock) || 0);
      });
      setProducts(list);
    } catch (e) {}
  }, [API_URL]);

  const filteredProducts = useMemo(() => {
    const q = productSearch.trim().toLowerCase();
    let list = products;
    if (q) {
      list = products.filter((p) => {
        const name = (p.name || '').toLowerCase();
        const cat = (p.category || '').toLowerCase();
        const brand = (p.brand || '').toLowerCase();
        const price = String(p.offerPrice ?? p.price ?? '');
        const desc = (p.description || '').toLowerCase();
        return (
          name.includes(q) ||
          cat.includes(q) ||
          brand.includes(q) ||
          price.includes(q) ||
          desc.includes(q)
        );
      });
    }
    const sel = selectedProductId
      ? products.find((x) => String(x._id) === String(selectedProductId))
      : null;
    if (sel && !list.some((x) => String(x._id) === String(selectedProductId))) {
      list = [sel, ...list];
    }
    return list;
  }, [products, productSearch, selectedProductId]);

  const fetchAiStatus = useCallback(async () => {
    try {
      const resp = await axios.get(`${API_URL}/social/ai-status`);
      setAiStatus(resp.data);
    } catch (e) {}
  }, [API_URL]);

  useEffect(() => {
    fetchConfig(); fetchHistory(); fetchProducts(); fetchAiStatus();
  }, [fetchConfig, fetchHistory, fetchProducts, fetchAiStatus]);

  useEffect(() => {
    if (reelWorkflowMode !== 'product') return;
    if (!selectedProductId) return;
    const p = products.find(x => String(x._id) === String(selectedProductId));
    if (p) setHighlights(buildHighlightsFromProduct(p));
  }, [reelWorkflowMode, selectedProductId, products]);

  // ─── Upload ───────────────────────────────────────────────────────────────────
  const uploadMedia = async () => {
    setLoadingUpload(true);
    try {
      const fd = new FormData();
      photoFiles.forEach(f => fd.append('photos', f));
      if (outroFile) fd.append('outro', outroFile);
      if (musicFile) fd.append('music', musicFile);
      if (!photoFiles.length && !outroFile && !musicFile) {
        showToast('Photos / outro / music me se kuch select karo', 'warning');
        return;
      }
      const resp = await axios.post(`${API_URL}/social/upload-media`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadResponse(resp.data);
      const q = resp.data?.quota;
      const mb = q?.tempUsedBytes != null ? (q.tempUsedBytes / (1024 * 1024)).toFixed(1) : null;
      const cap = q?.tempMaxBytes != null ? Math.round(q.tempMaxBytes / (1024 * 1024)) : 90;
      showToast(
        `Upload OK — ${resp.data.photoPaths?.length || 0} photo(s)${mb != null ? ` · temp ~${mb}/${cap} MB` : ''}`,
        'success'
      );
    } catch (e) {
      showToast(socialApiErr(e, 'Upload failed'), 'danger');
    } finally { setLoadingUpload(false); }
  };

  // ─── Preview (Step 5) ─────────────────────────────────────────────────────────
  const handlePreviewScript = async () => {
    if (reelWorkflowMode === 'promo') {
      const brief = promoScriptBrief.trim();
      if (brief.length < 20) {
        showToast('Promo brief kam se kam 20 characters likho (shop / EMI / variety)', 'warning');
        return;
      }
      if (!(uploadResponse?.photoPaths?.length || uploadResponse?.photos?.length)) {
        showToast('Pehle Step 1 se kam se ek photo upload karo', 'warning');
        return;
      }
      setLoadingPreview(true);
      try {
        const resp = await axios.post(`${API_URL}/social/preview-content`, {
          reelMode: 'promo',
          scriptBrief: brief,
          promoTone,
        });
        const d = resp.data;
        setPreviewData(d);
        setEditedVoiceover(d.voiceoverScript || '');
        setEditedCaption(d.generatedCaption || '');
        const st = Array.isArray(d.slideTitles) && d.slideTitles.length === 6
          ? d.slideTitles
          : [...EMPTY_SLIDE_TITLES];
        setEditedSlideTitles(st);
        if (d.aiProvider) setAiStatus(s => ({ ...s, primary: d.aiProvider }));
        showToast('Promo script ready — edit karke Step 6 par generate karo', 'success');
      } catch (e) {
        showToast(socialApiErr(e, 'Preview failed'), 'danger');
      } finally { setLoadingPreview(false); }
      return;
    }

    if (!selectedProductId) { showToast('Pehle product choose karo (Step 1)', 'warning'); return; }
    setLoadingPreview(true);
    try {
      const resp = await axios.post(`${API_URL}/social/preview-content`, {
        reelMode: 'product',
        productId: selectedProductId,
        highlights: highlights.filter(h => h.label || h.value),
      });
      const d = resp.data;
      setPreviewData(d);
      setEditedVoiceover(d.voiceoverScript || '');
      setEditedCaption(d.generatedCaption || '');
      const st = Array.isArray(d.slideTitles) && d.slideTitles.length === 6
        ? d.slideTitles
        : [...EMPTY_SLIDE_TITLES];
      setEditedSlideTitles(st);
      if (d.aiProvider) setAiStatus(s => ({ ...s, primary: d.aiProvider }));
      showToast('Script ready hai — edit karke Continue karo', 'success');
    } catch (e) {
      showToast(socialApiErr(e, 'Preview failed'), 'danger');
    } finally { setLoadingPreview(false); }
  };

  // ─── Build body for generate calls ───────────────────────────────────────────
  const buildGenerateBody = () => {
    const body = {
      highlights: highlights.filter(h => h.label || h.value),
      voiceEnabled,
      voiceGender,
      useDefaultMusic,
      reelTheme,
    };
    if (reelWorkflowMode === 'promo') {
      body.reelMode = 'promo';
      body.scriptBrief = promoScriptBrief.trim();
      body.promoTone = promoTone;
      if (previewData?.slideSubtitles?.length === 6) {
        body.slideSubtitles = previewData.slideSubtitles;
      }
    } else {
      body.reelMode = 'product';
      if (selectedProductId) body.productId = selectedProductId;
    }
    if (uploadResponse?.photoPaths?.length) body.photoPaths = uploadResponse.photoPaths;
    if (uploadResponse?.photos?.length) body.photos = uploadResponse.photos;
    if (uploadResponse?.outroPath) body.outroPath = uploadResponse.outroPath;
    else if (uploadResponse?.outro) body.outroPath = uploadResponse.outro;
    if (uploadResponse?.musicPath) body.musicPath = uploadResponse.musicPath;
    else if (uploadResponse?.music) body.musicPath = uploadResponse.music;

    if (editedVoiceover.trim()) body.customVoiceoverScript = editedVoiceover.trim();
    if (editedCaption.trim()) body.customCaption = editedCaption.trim();
    if (editedSlideTitles.some(s => s.trim())) body.customSlideTitles = editedSlideTitles;

    return body;
  };

  // ─── Generate reel ────────────────────────────────────────────────────────────
  const handleGenerateReel = async () => {
    setLoadingGenerate(true); setLastResult(null); setLastAction('generate');
    try {
      const resp = await axios.post(`${API_URL}/social/generate-reel`, buildGenerateBody());
      setLastResult(resp.data);
      showToast('Reel ban gayi!', 'success');
      await fetchHistory();
    } catch (e) {
      const errMsg = e?.response?.data?.message || e?.message || 'Reel generation failed';
      showToast(errMsg, 'danger'); setLastResult({ overallStatus: 'failed', error: errMsg });
    } finally { setLoadingGenerate(false); }
  };

  // ─── Generate post image ──────────────────────────────────────────────────────
  const handleGeneratePost = async () => {
    setLoadingPost(true); setLastResult(null); setLastAction('post');
    try {
      const body = {
        productId: selectedProductId,
        highlights: highlights.filter(h => h.label || h.value),
      };
      if (uploadResponse?.photoPaths?.length) body.photoPaths = uploadResponse.photoPaths;
      if (uploadResponse?.photos?.length) body.photos = uploadResponse.photos;
      const resp = await axios.post(`${API_URL}/social/generate-post`, body);
      setLastResult(resp.data);
      showToast('Post image ban gayi!', 'success');
      await fetchHistory();
    } catch (e) {
      const errMsg = e?.response?.data?.message || e?.message || 'Post failed';
      showToast(errMsg, 'danger'); setLastResult({ overallStatus: 'failed', error: errMsg });
    } finally { setLoadingPost(false); }
  };

  // ─── Generate & post social ───────────────────────────────────────────────────
  const handleGenerateAndPostSocial = async () => {
    setLoadingPost(true); setLastResult(null); setLastAction('social');
    try {
      const resp = await axios.post(`${API_URL}/social/generate-and-post`, buildGenerateBody());
      setLastResult(resp.data);
      showToast(resp.data?.message || 'Done!', 'success');
      await fetchHistory();
    } catch (e) {
      const errMsg = e?.response?.data?.message || e?.message || 'Failed';
      showToast(errMsg, 'danger'); setLastResult({ overallStatus: 'failed', error: errMsg });
    } finally { setLoadingPost(false); }
  };

  const handleSaveSchedule = async () => {
    setLoadingSave(true);
    try {
      await axios.post(`${API_URL}/social/config`, { autoPostEnabled: cfg.autoPostEnabled, dailyPostTime: cfg.dailyPostTime });
      showToast('Schedule saved!', 'success');
    } catch (e) { showToast('Schedule save failed', 'danger'); }
    finally { setLoadingSave(false); }
  };

  const updateHighlight = (idx, field, val) => {
    setHighlights(prev => {
      const next = [...prev];
      if (!next[idx]) next[idx] = { label: '', value: '' };
      next[idx] = { ...next[idx], [field]: val };
      return next;
    });
  };

  const busy = loadingGenerate || loadingPost || loadingUpload || loadingPreview;

  const aiInfo = AI_LABELS[aiStatus.primary] || AI_LABELS.fallback;

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="container-fluid py-3">
      {toast.show && (
        <div
          className={`alert alert-${toast.type} alert-dismissible position-fixed shadow`}
          style={{ top: 80, right: 20, zIndex: 9999, minWidth: 300, maxWidth: 420 }}
          role="alert"
        >
          {toast.msg}
          <button type="button" className="btn-close" onClick={() => setToast(t => ({ ...t, show: false }))} />
        </div>
      )}

      <div className="d-flex align-items-center gap-2 mb-4">
        <i className="bi bi-camera-reels fs-3 text-primary" />
        <div>
          <h4 className="mb-0">Social Media — Reel &amp; Post</h4>
          <small className="text-muted">
            <strong>Product reel</strong> ya <strong>Shop promo reel</strong> (brief + photos) → voice/theme → script edit → generate
          </small>
        </div>
      </div>

      <div className="row g-3">
        {/* ── Wizard ── */}
        <div className="col-12 col-lg-8">
          <div className="card">
            <div className="card-header bg-primary text-white d-flex flex-wrap align-items-center justify-content-between gap-2">
              <strong>Step {step} / {maxStep}</strong>
              <div className="btn-group btn-group-sm">
                {[1, 2, 3, 4, 5, 6].map(s => (
                  <button key={s} type="button"
                    className={`btn ${step === s ? 'btn-light' : 'btn-outline-light'}`}
                    onClick={() => setStep(s)}
                  >{s}</button>
                ))}
              </div>
            </div>

            <div className="card-body">
              {/* ── Step 1: Product OR Promo brief + media ── */}
              {step === 1 && (
                <div>
                  <h6 className="fw-semibold mb-2">1. Reel type + setup</h6>
                  <div className="btn-group w-100 mb-3" role="group" aria-label="Reel workflow">
                    <button
                      type="button"
                      className={`btn btn-sm ${reelWorkflowMode === 'product' ? 'btn-primary' : 'btn-outline-primary'}`}
                      disabled={busy}
                      onClick={() => {
                        setReelWorkflowMode('product');
                        clearPreviewState();
                      }}
                    >
                      Ek product reel
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${reelWorkflowMode === 'promo' ? 'btn-primary' : 'btn-outline-primary'}`}
                      disabled={busy}
                      onClick={() => {
                        setReelWorkflowMode('promo');
                        clearPreviewState();
                      }}
                    >
                      Shop / general promo
                    </button>
                  </div>

                  {reelWorkflowMode === 'product' ? (
                    <>
                      <p className="text-muted small mb-2">
                        Naye products upar (latest pehle). Neeche search se naam / category / brand / price se filter karo.
                      </p>
                      <div className="input-group mb-2">
                        <span className="input-group-text"><i className="bi bi-search" aria-hidden="true" /></span>
                        <input
                          type="search"
                          className="form-control"
                          placeholder="Search: iPhone, Airpods, 17999, Old Mobile…"
                          value={productSearch}
                          onChange={(e) => setProductSearch(e.target.value)}
                          disabled={busy}
                          autoComplete="off"
                          aria-label="Product search"
                        />
                        {productSearch && (
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => setProductSearch('')}
                            disabled={busy}
                          >
                            Clear
                          </button>
                        )}
                      </div>
                      <div className="d-flex justify-content-between align-items-center small text-muted mb-1">
                        <span>
                          {filteredProducts.length === products.length
                            ? `${products.length} products (latest first)`
                            : `${filteredProducts.length} match · ${products.length} total`}
                        </span>
                      </div>
                      <select
                        className="form-select"
                        value={selectedProductId}
                        onChange={(e) => setSelectedProductId(e.target.value)}
                        disabled={busy}
                        size={Math.min(14, Math.max(6, filteredProducts.length + 1))}
                        style={{ maxHeight: 360 }}
                      >
                        <option value="">— Product choose karo —</option>
                        {filteredProducts.map((p) => (
                          <option key={p._id} value={p._id}>
                            {p.name} — ₹{(p.offerPrice || p.price || 0).toLocaleString('en-IN')} | {p.category}
                            {(p.stock || 0) <= 0 ? ' | stock:0' : ''}
                          </option>
                        ))}
                      </select>
                      <p className="text-muted small mt-2 mb-0">Select ke baad highlights auto-fill (Step 3). Photos Step 2 se upload karo.</p>
                    </>
                  ) : (
                    <>
                      <p className="text-muted small mb-2">
                        Yahan likho aap <strong>kaisaa message</strong> dena chahte ho (variety, EMI, premium iPhones, trust…).
                        AI professional voiceover + 6 slides banayega — phir Step 5 me edit / confirm.
                      </p>
                      <label className="form-label small fw-semibold mb-1">Script / message brief (zaroori)</label>
                      <textarea
                        className="form-control mb-3"
                        rows={5}
                        placeholder="Example: Hamare paas sabhi prakar ke phone milte hain — budget se lekar premium iPhones tak. Purane premium iPhones QC checked. Easy EMI bhi available. Punjab me trusted shop A2ZTAK…"
                        value={promoScriptBrief}
                        onChange={(e) => setPromoScriptBrief(e.target.value)}
                        disabled={busy}
                        style={{ fontSize: '0.92rem', lineHeight: 1.55 }}
                      />
                      <div className="row g-2 mb-3">
                        <div className="col-md-6">
                          <label className="form-label small fw-semibold mb-1">Tone / style</label>
                          <select
                            className="form-select form-select-sm"
                            value={promoTone}
                            onChange={(e) => setPromoTone(e.target.value)}
                            disabled={busy}
                          >
                            <option value="professional">Professional (calm, credible)</option>
                            <option value="energetic">Energetic</option>
                            <option value="friendly">Friendly</option>
                            <option value="trust">Trust / honesty focus</option>
                          </select>
                        </div>
                      </div>

                      <label className="form-label small mb-1 fw-semibold">Promo photos (max 5) <span className="text-muted fw-normal">— kam se ek zaroori</span></label>
                      <input type="file" className="form-control mb-2" accept="image/*" multiple
                        onChange={e => setPhotoFiles(Array.from(e.target.files || []).slice(0, 5))} disabled={busy} />

                      <label className="form-label small mb-1 fw-semibold">Outro (optional)</label>
                      <input type="file" className="form-control mb-2" accept="image/*"
                        onChange={e => setOutroFile(e.target.files?.[0] || null)} disabled={busy} />

                      <label className="form-label small mb-1 fw-semibold">Background music (optional)</label>
                      <input type="file" className="form-control mb-2" accept="audio/mpeg,audio/*"
                        onChange={e => setMusicFile(e.target.files?.[0] || null)} disabled={busy} />

                      {photoFiles.length > 0 && (
                        <div className="d-flex flex-wrap gap-2 mb-2">
                          {photoFiles.map((f, i) => (
                            <span key={i} className="badge bg-secondary">{f.name}</span>
                          ))}
                        </div>
                      )}

                      <button type="button" className="btn btn-outline-primary btn-sm"
                        onClick={uploadMedia} disabled={busy}>
                        {loadingUpload ? <><span className="spinner-border spinner-border-sm me-1" />Uploading...</> : 'Upload to server'}
                      </button>
                      {uploadResponse?.jobId && (
                        <div className="alert alert-success mt-2 py-2 small mb-0">
                          ✓ {uploadResponse.photoPaths?.length || 0} photo(s) ready — ab Step 5 par script generate karo
                        </div>
                      )}
                      <p className="text-muted small mt-2 mb-0">
                        Step 2 par agar aur media chahiye ho to wahan bhi upload kar sakte ho. Storage limit pehle jaisa (~90 MB temp).
                      </p>
                    </>
                  )}
                </div>
              )}

              {/* ── Step 2: Photos + Media ── */}
              {step === 2 && (
                <div>
                  <h6 className="fw-semibold mb-3">2. Photos + Outro + Music (optional)</h6>
                  {reelWorkflowMode === 'promo' && (
                    <div className="alert alert-secondary py-2 small mb-3">
                      <strong>Promo mode:</strong> agar Step 1 me photos + music upload ho chuke hain to is step ko skip karke Next kar sakte ho.
                      Warna yahan se bhi files add karke <strong>Upload to server</strong> dabao.
                    </div>
                  )}
                  <label className="form-label small mb-1 fw-semibold">Product photos (max 5) <span className="text-muted fw-normal">— saari photos reel ke alag alag slides par aayengi</span></label>
                  <input type="file" className="form-control mb-3" accept="image/*" multiple
                    onChange={e => setPhotoFiles(Array.from(e.target.files || []).slice(0, 5))} disabled={busy} />

                  <label className="form-label small mb-1 fw-semibold">Outro slide (last frame)</label>
                  <input type="file" className="form-control mb-3" accept="image/*"
                    onChange={e => setOutroFile(e.target.files?.[0] || null)} disabled={busy} />

                  <label className="form-label small mb-1 fw-semibold">Background music (.mp3)</label>
                  <input type="file" className="form-control mb-3" accept="audio/mpeg,audio/*"
                    onChange={e => setMusicFile(e.target.files?.[0] || null)} disabled={busy} />

                  {photoFiles.length > 0 && (
                    <div className="d-flex flex-wrap gap-2 mb-2">
                      {photoFiles.map((f, i) => (
                        <span key={i} className="badge bg-secondary">{f.name}</span>
                      ))}
                    </div>
                  )}

                  <button type="button" className="btn btn-outline-primary"
                    onClick={uploadMedia} disabled={busy}>
                    {loadingUpload ? <><span className="spinner-border spinner-border-sm me-1" />Uploading...</> : 'Upload to server'}
                  </button>

                  {uploadResponse?.jobId && (
                    <div className="alert alert-success mt-3 py-2 small mb-0">
                      ✓ Upload OK — {uploadResponse.photoPaths?.length || 0} photo(s) ready
                    </div>
                  )}
                  <p className="text-muted small mt-2 mb-0">
                    Skip karo to product ki main image + default outro/music use hogi.
                    <br />
                    <strong>Storage:</strong> temp uploads ~<strong>90 MB</strong> tak; 24h+ purane auto-clear.
                    Full ho to pehle history se reel download karke phir upload karo.
                  </p>
                </div>
              )}

              {/* ── Step 3: Highlights ── */}
              {step === 3 && (
                <div>
                  <h6 className="fw-semibold mb-1">3. Highlights (Display, Battery, Camera…)</h6>
                  {reelWorkflowMode === 'promo' ? (
                    <p className="text-muted small mb-3">
                      <strong>Promo reel</strong> me zyatar text AI brief se aata hai. Yahan optional extra bullets add kar sakte ho agar on-screen par kuch aur dikhana ho; khali chhod sakte ho.
                    </p>
                  ) : (
                    <p className="text-muted small mb-3">Ye AI ko accurate specs denge. OpenAI known model specs bhi khud use karta hai.</p>
                  )}
                  {highlights.map((h, idx) => (
                    <div key={idx} className="row g-2 mb-2 align-items-end">
                      <div className="col-4">
                        <input className="form-control form-control-sm" placeholder="Label (e.g. Battery)"
                          value={h.label} onChange={e => updateHighlight(idx, 'label', e.target.value)} disabled={busy} />
                      </div>
                      <div className="col-7">
                        <input className="form-control form-control-sm" placeholder="Value (e.g. 5000 mAh)"
                          value={h.value} onChange={e => updateHighlight(idx, 'value', e.target.value)} disabled={busy} />
                      </div>
                      <div className="col-1">
                        <button type="button" className="btn btn-sm btn-outline-danger"
                          onClick={() => setHighlights(prev => prev.filter((_, i) => i !== idx))}>×</button>
                      </div>
                    </div>
                  ))}
                  <button type="button" className="btn btn-sm btn-outline-secondary mt-1"
                    onClick={() => setHighlights(prev => [...prev, { label: '', value: '' }])}>+ Row add karo</button>
                </div>
              )}

              {/* ── Step 4: Voice + Music + Theme ── */}
              {step === 4 && (
                <div>
                  <h6 className="fw-semibold mb-3">4. Voice + Music + Theme</h6>
                  <div className="form-check form-switch mb-3">
                    <input className="form-check-input" type="checkbox" id="voiceEn"
                      checked={voiceEnabled} onChange={e => setVoiceEnabled(e.target.checked)} disabled={busy} />
                    <label className="form-check-label" htmlFor="voiceEn">Hindi voiceover (Edge TTS) — reel ke andar voice chalegi</label>
                  </div>
                  {voiceEnabled && (
                    <div className="mb-3">
                      <label className="form-label small fw-semibold">Voice</label>
                      <select className="form-select" value={voiceGender}
                        onChange={e => setVoiceGender(e.target.value)} disabled={busy}>
                        <option value="female">Female — hi-IN-Swara</option>
                        <option value="male">Male — hi-IN-Madhur</option>
                      </select>
                    </div>
                  )}
                  <div className="form-check form-switch mb-3">
                    <input className="form-check-input" type="checkbox" id="defMusic"
                      checked={useDefaultMusic} onChange={e => setUseDefaultMusic(e.target.checked)} disabled={busy} />
                    <label className="form-check-label" htmlFor="defMusic">Default background music use karo</label>
                  </div>
                  <p className="text-muted small mt-0 mb-3">Apna music Step 2 mein upload kar sakte ho.</p>

                  {/* ─── Theme Selector ─── */}
                  <label className="form-label small fw-semibold mb-2">Reel Visual Theme</label>
                  <div className="d-flex flex-wrap gap-2 mb-1">
                    {[
                      { key: 'auto',      label: '🎲 Random',   bg: '#1e293b', accent: '#ffffff' },
                      { key: 'classic',   label: '🟡 Classic',  bg: '#0f172a', accent: '#FACC15' },
                      { key: 'neon',      label: '🟢 Neon',     bg: '#050a05', accent: '#22C55E' },
                      { key: 'luxury',    label: '✨ Luxury',   bg: '#0a0700', accent: '#D97706' },
                      { key: 'fire',      label: '🔴 Fire',     bg: '#1a0000', accent: '#EF4444' },
                      { key: 'royal',     label: '💜 Royal',    bg: '#1e0a3c', accent: '#A855F7' },
                      { key: 'cyber',     label: '🔵 Cyber',    bg: '#001a1a', accent: '#06B6D4' },
                      { key: 'sunset',    label: '🟠 Sunset',   bg: '#1c0a00', accent: '#F97316' },
                      { key: 'corporate', label: '🔷 Corporate',bg: '#1e293b', accent: '#3B82F6' },
                      { key: 'forest',    label: '🌿 Forest',   bg: '#052e16', accent: '#84CC16' },
                    ].map(th => (
                      <button
                        key={th.key}
                        type="button"
                        onClick={() => setReelTheme(th.key)}
                        disabled={busy}
                        style={{
                          background: th.bg,
                          border: `2px solid ${reelTheme === th.key ? th.accent : 'transparent'}`,
                          color: th.accent,
                          borderRadius: 8,
                          padding: '5px 12px',
                          fontSize: 12,
                          fontWeight: reelTheme === th.key ? 700 : 400,
                          cursor: 'pointer',
                          boxShadow: reelTheme === th.key ? `0 0 6px ${th.accent}88` : 'none',
                          transition: 'all 0.15s',
                        }}
                      >
                        {th.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-muted small mb-0">
                    {reelTheme === 'auto'
                      ? 'Har reel ek nayi random theme se banegi — variety milegi viewers ko.'
                      : `Selected: "${reelTheme}" theme — har slide is style mein banega.`}
                  </p>
                </div>
              )}

              {/* ── Step 5: Script Preview & Edit ── */}
              {step === 5 && (
                <div>
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                    <div>
                      <h6 className="fw-semibold mb-0">5. AI Script — Preview &amp; Edit</h6>
                      <small className="text-muted">Voiceover text aur slide titles dekho, edit karo, tab generate karo.</small>
                    </div>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={handlePreviewScript}
                      disabled={
                        busy ||
                        (reelWorkflowMode === 'product'
                          ? !selectedProductId
                          : promoScriptBrief.trim().length < 20 ||
                            !(uploadResponse?.photoPaths?.length || uploadResponse?.photos?.length))
                      }
                    >
                      {loadingPreview
                        ? <><span className="spinner-border spinner-border-sm me-1" />Generating script...</>
                        : previewData ? '↺ Regenerate' : 'Generate Script'}
                    </button>
                  </div>

                  {!previewData && !loadingPreview && (
                    <div className="alert alert-info py-2 small mb-0">
                      {reelWorkflowMode === 'promo'
                        ? '↑ Brief + uploaded photos ke hisaab se AI professional script banayega — phir neeche edit karke Step 6 par final reel generate karo.'
                        : '↑ "Generate Script" dabao — AI voiceover text + 6 slide titles banayega jo tum edit kar sakte ho.'}
                    </div>
                  )}

                  {previewData && (
                    <>
                      <div className="d-flex align-items-center gap-2 mb-3">
                        <span className="text-muted small">Generated by:</span>
                        <span className={`badge ${AI_LABELS[previewData.aiProvider]?.badge || 'bg-secondary'}`}>
                          {AI_LABELS[previewData.aiProvider]?.icon} {AI_LABELS[previewData.aiProvider]?.label || previewData.aiProvider}
                        </span>
                      </div>

                      {/* Voiceover Script */}
                      <div className="mb-3">
                        <label className="form-label fw-semibold small">
                          🎙️ Voiceover Script
                          <span className="text-muted fw-normal ms-1">(yahi TTS voice se bolta hai reel mein)</span>
                        </label>
                        <textarea
                          className="form-control"
                          rows={5}
                          value={editedVoiceover}
                          onChange={e => setEditedVoiceover(e.target.value)}
                          placeholder="Voiceover script yahaan dikhega…"
                          style={{ fontSize: '0.92rem', lineHeight: 1.6 }}
                        />
                        <div className="d-flex justify-content-between mt-1">
                          <small className="text-muted">{editedVoiceover.split(/\s+/).filter(Boolean).length} words — ~{Math.ceil(editedVoiceover.split(/\s+/).filter(Boolean).length / 3)}s bolne mein</small>
                          <button className="btn btn-link btn-sm p-0 text-danger"
                            onClick={() => setEditedVoiceover(previewData.voiceoverScript || '')}>Reset</button>
                        </div>
                      </div>

                      {/* Slide Titles */}
                      <div className="mb-3">
                        <label className="form-label fw-semibold small">
                          🖼️ 6 Slide Titles
                          <span className="text-muted fw-normal ms-1">(reel ke bottom panel pe ye text dikhenga)</span>
                        </label>
                        <div className="row g-2">
                          {editedSlideTitles.map((t, i) => (
                            <div key={i} className="col-12 col-md-6">
                              <div className="input-group input-group-sm">
                                <span className="input-group-text text-muted" style={{ minWidth: 60 }}>Slide {i + 1}</span>
                                <input
                                  className="form-control"
                                  value={t}
                                  onChange={e => {
                                    const next = [...editedSlideTitles];
                                    next[i] = e.target.value;
                                    setEditedSlideTitles(next);
                                  }}
                                  maxLength={60}
                                  placeholder={['Product hook', 'Spec 1', 'Spec 2', 'Spec 3', 'Price', 'CTA'][i]}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                        <button className="btn btn-link btn-sm p-0 text-danger mt-1"
                          onClick={() => setEditedSlideTitles(previewData.slideTitles?.length === 6 ? [...previewData.slideTitles] : [...EMPTY_SLIDE_TITLES])}>
                          Reset slide titles
                        </button>
                      </div>

                      {/* Caption */}
                      <div className="mb-2">
                        <label className="form-label fw-semibold small">
                          📝 Instagram Caption
                          <span className="text-muted fw-normal ms-1">({editedCaption.length}/220 chars)</span>
                        </label>
                        <textarea
                          className="form-control"
                          rows={3}
                          value={editedCaption}
                          onChange={e => setEditedCaption(e.target.value)}
                          maxLength={220}
                          style={{ fontSize: '0.88rem' }}
                        />
                      </div>

                      {/* Hashtags preview */}
                      {previewData.hashtags?.length > 0 && (
                        <div className="mb-0">
                          <small className="text-muted fw-semibold">Hashtags: </small>
                          <small className="text-muted">{previewData.hashtags.slice(0, 12).join(' ')}</small>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* ── Step 6: Generate ── */}
              {step === 6 && (
                <div>
                  <h6 className="fw-semibold mb-1">6. Generate</h6>
                  <p className="text-muted small mb-3">
                    {editedVoiceover
                      ? '✓ Custom script (Step 5 se) use hoga'
                      : reelWorkflowMode === 'promo'
                        ? '⚠ Promo ke liye Step 5 mein script generate + edit zaroor karo — taaki galat message na jaye.'
                        : '⚠ Step 5 mein pehle script preview karo — ya seedha generate bhi kar sakte ho (AI khud generate karega)'}
                  </p>

                  {editedVoiceover && (
                    <div className="alert alert-success py-2 small mb-3">
                      <strong>Script ready:</strong> "{editedVoiceover.slice(0, 80)}…"
                    </div>
                  )}

                  <div className="d-flex flex-wrap gap-2">
                    <button type="button" className="btn btn-primary"
                      onClick={handleGenerateReel} disabled={busy}>
                      {loadingGenerate
                        ? <><span className="spinner-border spinner-border-sm me-1" />Generating reel…</>
                        : '🎬 Generate Reel (download)'}
                    </button>
                    <button type="button" className="btn btn-success"
                      onClick={handleGeneratePost} disabled={busy || !selectedProductId || reelWorkflowMode === 'promo'}>
                      {loadingPost && lastAction === 'post'
                        ? <><span className="spinner-border spinner-border-sm me-1" />…</>
                        : '🖼 Generate Post Image'}
                    </button>
                    <button type="button" className="btn btn-outline-success"
                      onClick={handleGenerateAndPostSocial} disabled={busy}>
                      🚀 Generate &amp; Post (social APIs)
                    </button>
                  </div>
                  <p className="text-muted small mt-3 mb-0">
                    <strong>Abhi (manual):</strong> pehla button — reel banegi, download + niche WhatsApp / Instagram caption copy karke khud upload karo.
                    {' '}
                    <strong>Baad mein (autopost):</strong> jab Instagram / YouTube tokens config mein laga doge, tab teesra button se direct upload try hoga; tab tak bina API ke bhi poora kaam chalta rahega.
                  </p>
                </div>
              )}

              {/* ── Nav ── */}
              <div className="d-flex justify-content-between mt-4">
                <button type="button" className="btn btn-outline-secondary btn-sm"
                  disabled={step <= 1 || busy} onClick={() => setStep(s => s - 1)}>← Back</button>
                <button type="button" className="btn btn-outline-primary btn-sm"
                  disabled={step >= maxStep || busy} onClick={() => setStep(s => s + 1)}>Next →</button>
              </div>

              {/* ── Result ── */}
              {lastResult && !busy && (
                <div className={`alert mt-3 mb-0 alert-${lastResult.overallStatus === 'success' || lastResult.overallStatus === 'partial' ? 'success' : 'danger'}`}>
                  <strong>{lastAction === 'post' ? 'Post:' : 'Result:'}</strong> <StatusBadge status={lastResult.overallStatus} />
                  {lastResult.productName && <div className="small mt-1">Product: {lastResult.productName}</div>}
                  {lastResult.error && <div className="text-danger small">{lastResult.error}</div>}
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    {lastResult.downloadUrl && (
                      <a href={lastResult.downloadUrl} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-primary">
                        ⬇ Download Reel
                      </a>
                    )}
                    {lastResult.outputType === 'post' && lastResult.socialPostId && (
                      <a href={`${API_URL}/social/post-image/${lastResult.socialPostId}`} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-success">
                        ⬇ Download Post Image
                      </a>
                    )}
                  </div>
                  {lastResult.manualPostCopy && (
                    <div className="mt-3 pt-3 border-top text-start">
                      <div className="fw-semibold mb-1">Manual upload — tayyar caption</div>
                      <p className="small text-muted mb-2">
                        Reel download karke WhatsApp Channel / Instagram pe daalte waqt niche se copy-paste karo.
                        Specs + condition product / highlights ke hisaab se bhare gaye hain.
                      </p>
                      <div className="mb-3">
                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-1">
                          <span className="small fw-semibold">WhatsApp Channel (formatting ke saath)</span>
                          <button
                            type="button"
                            className="btn btn-sm btn-success"
                            onClick={() => copyManualPost(lastResult.manualPostCopy.whatsapp, 'WhatsApp')}
                          >
                            Copy WhatsApp
                          </button>
                        </div>
                        <textarea
                          className="form-control form-control-sm font-monospace"
                          rows={11}
                          readOnly
                          value={lastResult.manualPostCopy.whatsapp}
                          style={{ fontSize: 12, resize: 'vertical' }}
                          onFocus={(e) => e.target.select()}
                        />
                      </div>
                      <div>
                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-1">
                          <span className="small fw-semibold">Instagram Reel caption + hashtags</span>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => copyManualPost(lastResult.manualPostCopy.instagram, 'Instagram')}
                          >
                            Copy Instagram
                          </button>
                        </div>
                        <textarea
                          className="form-control form-control-sm font-monospace"
                          rows={9}
                          readOnly
                          value={lastResult.manualPostCopy.instagram}
                          style={{ fontSize: 12, resize: 'vertical' }}
                          onFocus={(e) => e.target.select()}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Sidebar ── */}
        <div className="col-12 col-lg-4">
          <div className="card mb-3">
            <div className="card-header fw-semibold">Daily Schedule</div>
            <div className="card-body">
              <div className="form-check form-switch mb-3">
                <input className="form-check-input" type="checkbox" id="autoPostToggle"
                  checked={cfg.autoPostEnabled}
                  onChange={e => setCfg(c => ({ ...c, autoPostEnabled: e.target.checked }))} disabled={loadingSave} />
                <label className="form-check-label" htmlFor="autoPostToggle">Auto post</label>
              </div>
              <input type="time" className="form-control mb-2" value={cfg.dailyPostTime}
                onChange={e => setCfg(c => ({ ...c, dailyPostTime: e.target.value }))} disabled={loadingSave} />
              <button type="button" className="btn btn-success w-100" onClick={handleSaveSchedule} disabled={loadingSave}>
                {loadingSave ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>

          <div className="card mb-3">
            <div className="card-header fw-semibold">AI Engine</div>
            <ul className="list-group list-group-flush small">
              <li className="list-group-item d-flex justify-content-between align-items-center">
                <span>{aiInfo.icon} {aiInfo.label}</span>
                <span className={`badge ${aiInfo.badge}`}>Primary</span>
              </li>
              {aiStatus.openai && aiStatus.primary !== 'openai' && (
                <li className="list-group-item d-flex justify-content-between">OpenAI <span className="badge bg-light text-dark border">Active</span></li>
              )}
              {aiStatus.gemini && (
                <li className="list-group-item d-flex justify-content-between text-muted">Gemini <span className="badge bg-light text-dark border">{aiStatus.primary === 'gemini' ? 'Active' : 'Fallback'}</span></li>
              )}
              {aiStatus.deepseek && (
                <li className="list-group-item d-flex justify-content-between text-muted">DeepSeek <span className="badge bg-light text-dark border">Fallback</span></li>
              )}
              <li className="list-group-item d-flex justify-content-between text-muted">Voice <span className="badge bg-success text-white">Edge TTS</span></li>
            </ul>
          </div>

          {/* Quick steps guide */}
          <div className="card">
            <div className="card-header fw-semibold">Quick Guide</div>
            <ul className="list-group list-group-flush small">
              {[
                ['1', 'Product ya promo brief + media'],
                ['2', 'Photos / music (optional extra)'],
                ['3', 'Highlights check/edit karo'],
                ['4', 'Voice + music set karo'],
                ['5', 'Script preview → edit → OK'],
                ['6', 'Generate Reel / Post'],
              ].map(([n, t]) => (
                <li key={n} className={`list-group-item d-flex gap-2 align-items-start ${String(step) === n ? 'bg-primary-subtle fw-semibold' : 'text-muted'}`}>
                  <span className="badge bg-primary rounded-pill">{n}</span>{t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* ── History ── */}
      <div className="card mt-3">
        <div className="card-header d-flex justify-content-between align-items-center">
          <strong>History</strong>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={fetchHistory} disabled={histLoading}>Refresh</button>
        </div>
        <div className="card-body p-0">
          {histLoading ? (
            <div className="text-center py-4 text-muted">Loading…</div>
          ) : history.length === 0 ? (
            <div className="text-center py-4 text-muted">No entries yet</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-striped mb-0 align-middle">
                <thead className="table-dark">
                  <tr>
                    <th>Date</th><th>Product</th><th>Type</th><th>Overall</th><th>Reel</th><th>Post</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map(p => (
                    <tr key={p._id}>
                      <td className="text-nowrap">{p.postDateKey}</td>
                      <td><div className="text-truncate" style={{ maxWidth: 160 }} title={p.productName}>{p.productName || '—'}</div></td>
                      <td><span className="badge bg-secondary">{p.outputType || 'reel'}</span></td>
                      <td><StatusBadge status={p.overallStatus} /></td>
                      <td>
                        {p.videoPath
                          ? <a href={`${API_URL}/social/video/${p._id}`} target="_blank" rel="noreferrer" className="btn btn-xs btn-outline-primary py-0 px-1 small">DL</a>
                          : '—'}
                      </td>
                      <td>
                        {p.postImagePath
                          ? <a href={`${API_URL}/social/post-image/${p._id}`} target="_blank" rel="noreferrer" className="btn btn-xs btn-outline-success py-0 px-1 small">DL</a>
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SocialMediaDashboard;
